/**
 * Pedidos — a fila de análise do Escritório.
 *
 * É a outra ponta do "Meus pedidos": aqui o CD abre o pedido (o que já avisa o
 * solicitante — o status vira "em análise"), preenche o que consegue separar e
 * decide. Tudo separado → romaneio na hora. Parte → o solicitante escolhe se
 * aceita assim ou cancela. Nada → recusa, com motivo.
 *
 * Replicado do sistema antigo por pedido do Lucas (04/08).
 */

import { api, ErroApi } from '../api.js';
import { dataHora, escapar, numero, quando } from '../comum/formato.js';
import { avisar, avisarBloqueante } from '../comum/aviso.js';

interface PedidoDaFila {
  numero: string;
  criado_em: string;
  status: string;
  solicitante_nome: string | null;
  obs: string | null;
  obra: string;
  obraNome: string;
  itens: number;
  /** Presentes só no histórico (rota /api/pedidos/todos). */
  motivo_recusa?: string | null;
  decidido_em?: string | null;
  romaneio?: string | null;
  romaneioStatus?: string | null;
  entregue_em?: string | null;
}

interface ItemDoPedido {
  codigo: string;
  nome: string;
  unidade: string | null;
  pedida: number;
  separada: number;
  disponivel: number;
  miniatura: string | null;
  foto: string | null;
  controlaUnidades: boolean;
  reservadoAte: string | null;
}

interface UnidadeDisponivel {
  patrimonio: string;
  numero_serie: string | null;
  estado: string;
  miniatura: string | null;
}

interface PedidoAberto {
  numero: string;
  criado_em: string;
  status: string;
  solicitante_nome: string | null;
  obs: string | null;
  obra: string;
  obraNome: string;
  itens: ItemDoPedido[];
}

export function montarPedidosFila(raiz: HTMLElement): void {
  /** Pessoas com o módulo motorista — para o seletor da separação. */
  let motoristas: { id: number; nome: string }[] | undefined;
  async function carregarMotoristas(): Promise<{ id: number; nome: string }[]> {
    if (!motoristas) {
      const r = await api.get<{ pessoas: { id: number; nome: string; modulos: string[] }[] }>(
        '/api/pessoas?status=ativo',
      );
      motoristas = r.pessoas.filter((x) => x.modulos.includes('motorista'));
    }
    return motoristas;
  }

  /** A aba atual: a fila viva ou o histórico (14/08, Lucas). */
  let situacao: 'abertos' | 'atendidos' | 'cancelados' = 'abertos';
  let pagina = 1;

  async function listar(): Promise<void> {
    raiz.innerHTML = `<p class="carregando">Carregando…</p>`;
    let r: { pedidos: PedidoDaFila[]; total: number; pagina: number; paginas: number };
    try {
      r = await api.get<typeof r>(`/api/pedidos/todos?situacao=${situacao}&pagina=${pagina}`);
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui carregar.',
      )}</p>`;
      return;
    }
    const fila = r.pedidos;

    const abas = `
      <div class="chips" id="pf-abas">
        <button class="chip" data-sit="abertos" aria-pressed="${situacao === 'abertos'}">📥 Em aberto</button>
        <button class="chip" data-sit="atendidos" aria-pressed="${situacao === 'atendidos'}">✓ Atendidos</button>
        <button class="chip" data-sit="cancelados" aria-pressed="${situacao === 'cancelados'}">✗ Cancelados</button>
      </div>`;

    const paginacao =
      r.paginas > 1
        ? `<div class="paginacao">
             <button class="btn btn-claro" id="pf-ant" ${r.pagina <= 1 ? 'disabled' : ''}>← Anterior</button>
             <span>Página ${r.pagina} de ${r.paginas} · ${r.total} pedido(s)</span>
             <button class="btn btn-claro" id="pf-prox" ${r.pagina >= r.paginas ? 'disabled' : ''}>Próxima →</button>
           </div>`
        : '';

    if (fila.length === 0) {
      raiz.innerHTML = `${abas}<div class="vazio"><p>${
        situacao === 'abertos'
          ? 'Nenhum pedido esperando. A fila está limpa.'
          : situacao === 'atendidos'
            ? 'Nenhum pedido atendido ainda.'
            : 'Nenhum pedido cancelado ou recusado.'
      }</p></div>`;
    } else if (situacao === 'abertos') {
      const analisar = fila.filter((p) => p.status !== 'separado_parcial');
      const esperando = fila.filter((p) => p.status === 'separado_parcial');
      raiz.innerHTML = `${abas}
        ${
          analisar.length
            ? `<div class="fila">${analisar.map(cartao).join('')}</div>`
            : `<div class="vazio"><p>Nada para analisar agora.</p></div>`
        }
        ${
          esperando.length
            ? `<h3 class="pf-secao">Aguardando o solicitante decidir (separação parcial)</h3>
               <div class="fila">${esperando.map(cartao).join('')}</div>`
            : ''
        }${paginacao}`;
    } else {
      raiz.innerHTML = `${abas}<div class="fila">${fila.map(cartao).join('')}</div>${paginacao}`;
    }

    raiz.querySelector('#pf-abas')!.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('[data-sit]');
      if (!b) return;
      situacao = b.dataset['sit'] as typeof situacao;
      pagina = 1;
      void listar();
    });
    raiz.querySelector('#pf-ant')?.addEventListener('click', () => {
      pagina--;
      void listar();
    });
    raiz.querySelector('#pf-prox')?.addEventListener('click', () => {
      pagina++;
      void listar();
    });
    raiz.querySelectorAll<HTMLElement>('[data-abrir]').forEach((el) => {
      el.addEventListener('click', () => void abrir(el.dataset['abrir']!));
    });
  }

  function cartao(p: PedidoDaFila): string {
    const tipo =
      p.status === 'em_analise'
        ? '📦 EM SEPARAÇÃO'
        : p.status === 'separado_parcial'
          ? 'PARCIAL — COM O SOLICITANTE'
          : p.status === 'aguardando_analise'
            ? 'AGUARDANDO SEPARAÇÃO'
            : p.status === 'recusado'
              ? '✗ RECUSADO PELO CD'
              : p.status === 'cancelado'
                ? '✗ CANCELADO PELO SOLICITANTE'
                : p.entregue_em
                  ? '✓ ATENDIDO — ENTREGUE'
                  : '✓ ATENDIDO';
    const detalhe =
      p.romaneio && ['separado_integral', 'aceito'].includes(p.status)
        ? `<span class="fila-quem">romaneio ${escapar(p.romaneio)}${
            p.romaneioStatus === 'entregue'
              ? ` · entregue ${p.entregue_em ? quando(p.entregue_em) : ''}`
              : p.romaneioStatus
                ? ` · ${escapar(p.romaneioStatus.replace(/_/g, ' '))}`
                : ''
          }</span>`
        : p.status === 'recusado' && p.motivo_recusa
          ? `<span class="fila-quem">motivo: ${escapar(p.motivo_recusa)}</span>`
          : '';
    return `
      <button class="fila-item" data-abrir="${escapar(p.numero)}">
        <span class="fila-corpo">
          <span class="fila-tipo">${tipo}</span>
          <b class="fila-titulo">${escapar(p.numero)} — ${escapar(p.obraNome)}</b>
          <span class="fila-meta">${p.itens} item(ns) · ${quando(p.criado_em)}</span>
          ${p.solicitante_nome ? `<span class="fila-quem">pediu: ${escapar(p.solicitante_nome)}</span>` : ''}
          ${detalhe}
        </span>
      </button>`;
  }

  async function abrir(numeroPedido: string): Promise<void> {
    raiz.innerHTML = `<p class="carregando">Abrindo ${escapar(numeroPedido)}…</p>`;
    let p: PedidoAberto;
    let listaMotoristas: { id: number; nome: string }[] = [];
    try {
      // Este GET já promove "aguardando análise" → "em análise" no servidor —
      // o solicitante vê a mudança na tela dele.
      [p, listaMotoristas] = await Promise.all([
        api.get<PedidoAberto>(`/api/pedidos/${encodeURIComponent(numeroPedido)}`),
        carregarMotoristas().catch(() => [] as { id: number; nome: string }[]),
      ]);
    } catch (erro) {
      avisar(erro instanceof ErroApi ? erro.message : 'Não consegui abrir o pedido.', 'atencao');
      return listar();
    }

    const decidivel = p.status === 'em_analise';

    raiz.innerHTML = `
      <button class="voltar" id="pf-voltar">← Toda a fila</button>
      <section class="ped-painel">
        <header class="ficha-topo">
          <div>
            <span class="rotulo">Pedido de ${escapar(p.solicitante_nome ?? 'solicitante')}</span>
            <h2>${escapar(p.numero)} — ${escapar(p.obraNome)}</h2>
            <span class="sub-inline">${dataHora(p.criado_em)}</span>
          </div>
        </header>
        ${p.obs ? `<p class="palpite-obs">📝 ${escapar(p.obs)}</p>` : ''}

        <div class="pf-itens">
          ${p.itens
            .map(
              (i) => `
            <div class="pf-linha ${i.controlaUnidades ? 'pf-ferramenta' : ''}" data-cod="${escapar(i.codigo)}"
                 ${i.controlaUnidades ? 'data-ferramenta' : ''}>
              <span class="ped-cfoto">${
                i.miniatura
                  ? `<img src="${i.miniatura}" alt="" ${i.foto ? `data-grande="${escapar(api.foto(i.foto) ?? '')}" data-fotos-de="${escapar(i.codigo)}"` : ''}>`
                  : '<span class="sem">—</span>'
              }</span>
              <span class="ped-cnome">
                <b>${escapar(i.nome)}</b>
                <span class="sub-inline">${escapar(i.codigo)} · pedido: <b>${numero(i.pedida)}</b> ${escapar(i.unidade ?? '')} · disponível: ${numero(i.disponivel)}</span>
                ${i.disponivel < i.pedida ? `<span class="ped-alerta">não há disponível para tudo</span>` : ''}
                ${
                  i.controlaUnidades
                    ? `<span class="sub-inline">🔧 ferramenta · reserva até <b>${
                        i.reservadoAte ? String(i.reservadoAte).slice(0, 10).split('-').reverse().join('/') : '?'
                      }</b> — escolha o(s) exemplar(es) e tire a foto da saída</span>`
                    : ''
                }
              </span>
              <span class="ped-cqtd">
                <input type="number" min="0" step="any" data-sep
                       value="${decidivel ? Math.min(i.pedida, Math.max(i.disponivel, 0)) : i.separada}"
                       ${decidivel ? '' : 'disabled'} aria-label="Quantidade a separar">
              </span>
              ${i.controlaUnidades && decidivel ? `<div class="pf-unidades" data-unidades><p class="dica">Carregando exemplares…</p></div>` : ''}
            </div>`,
            )
            .join('')}
        </div>

        ${
          p.status === 'aguardando_analise'
            ? `<div class="conf-acoes">
                 <button class="btn btn-grande" id="pf-iniciar">▶️ Iniciar separação</button>
                 <button class="btn btn-claro" id="pf-recusar">Recusar…</button>
               </div>
               <p class="dica">Iniciar marca o pedido como <b>em separação</b> (o solicitante fica sabendo)
                 e libera o confirmar — com o motorista, que é obrigatório.</p>`
            : decidivel
            ? `<div class="conf-acoes">
                 <label class="rotulado pf-motorista">
                   <span>Motorista</span>
                   <select id="pf-motorista">
                     <option value="">— escolha o motorista —</option>
                     ${listaMotoristas
                       .map((m) => `<option value="${escapar(m.nome)}">${escapar(m.nome)}</option>`)
                       .join('')}
                     <option value="__avulso__">✏️ Outro (sem cadastro)</option>
                   </select>
                   <input id="pf-motorista-avulso" hidden maxlength="120"
                          placeholder="Nome do motorista sem cadastro">
                   <span class="dica">Motorista cadastrado vê o romaneio no app dele na hora — e recebe o aviso no celular.</span>
                 </label>
                 <button class="btn btn-grande" id="pf-separar">✓ Confirmar separação</button>
                 <button class="btn btn-claro" id="pf-recusar">Recusar…</button>
               </div>
               <p class="dica">Separou tudo → o romaneio é criado agora. Separou parte → o solicitante decide se aceita assim.</p>`
            : `<p class="dica">${
                p.status === 'separado_parcial'
                  ? 'Separação parcial registrada — aguardando a decisão do solicitante.'
                  : ['separado_integral', 'aceito'].includes(p.status)
                    ? '✓ Pedido atendido — acompanhe o romaneio na aba Romaneios.'
                    : p.status === 'recusado'
                      ? '✗ Pedido recusado pelo CD.'
                      : p.status === 'cancelado'
                        ? '✗ Pedido cancelado pelo solicitante — o que faltou voltou ao carrinho dele.'
                        : 'Pedido encerrado.'
              }</p>`
        }
        <p class="aviso" id="pf-erro" hidden></p>
      </section>`;

    raiz.querySelector('#pf-voltar')!.addEventListener('click', () => void listar());
    const erro = raiz.querySelector<HTMLElement>('#pf-erro')!;
    const mostrarErro = (e: unknown): void => {
      if (e instanceof ErroApi) {
        erro.innerHTML = `${escapar(e.message)}${
          e.detalhes?.length ? `<br>· ${e.detalhes.map(escapar).join('<br>· ')}` : ''
        }${e.sugestao ? `<br><i>${escapar(e.sugestao)}</i>` : ''}`;
      } else {
        erro.textContent = 'Não consegui registrar. Tente de novo.';
      }
      erro.hidden = false;
    };

    // Iniciar e Recusar existem ANTES do pedido ser decidível (aguardando separação),
    // então são ligados aqui, antes do `return` abaixo — 18/08: o botão Iniciar nasceu
    // depois do return e ficou surdo (renderizava, mas o clique não fazia nada).
    raiz.querySelector('#pf-iniciar')?.addEventListener('click', () => {
      void api
        .post(`/api/pedidos/${encodeURIComponent(p.numero)}/iniciar`, {})
        .then(() => {
          avisar('Pedido em separação — o solicitante foi avisado.');
          return abrir(p.numero);
        })
        .catch((e) => {
          avisar(e instanceof ErroApi ? e.message : 'Não consegui iniciar.', 'atencao');
          return abrir(p.numero);
        });
    });

    raiz.querySelector('#pf-recusar')?.addEventListener('click', () => {
      const motivo = prompt('Motivo da recusa — é o que o solicitante vai ler:');
      if (motivo === null) return;
      if (motivo.trim().length < 3) {
        avisarBloqueante('A recusa precisa de um motivo.');
        return;
      }
      void api
        .post(`/api/pedidos/${encodeURIComponent(p.numero)}/recusa`, { motivo: motivo.trim() })
        .then(() => {
          avisar('Pedido recusado — o solicitante vê o motivo.');
          return listar();
        })
        .catch(mostrarErro);
    });

    if (!decidivel) return;

    // FERRAMENTAS: carrega os exemplares disponíveis de cada linha e liga a
    // escolha (checkbox por patrimônio + foto da saída de cada um).
    const fotosPorPatrimonio = new Map<string, string>();
    raiz.querySelectorAll<HTMLElement>('[data-ferramenta]').forEach((linha) => {
      const codigo = linha.dataset['cod']!;
      const caixa = linha.querySelector<HTMLElement>('[data-unidades]')!;
      void api
        .get<{ unidades: UnidadeDisponivel[] }>(
          `/api/ferramentas?item=${encodeURIComponent(codigo)}&situacao=no_cd`,
        )
        .then((r) => {
          if (r.unidades.length === 0) {
            caixa.innerHTML = `<p class="ped-alerta">Nenhum exemplar no CD — cadastre em Ferramentas ou deixe 0 para ficar de fora.</p>`;
            return;
          }
          caixa.innerHTML = r.unidades
            .map(
              (u) => `
            <label class="pf-unidade">
              <input type="checkbox" data-un="${escapar(u.patrimonio)}">
              ${u.miniatura ? `<img src="${u.miniatura}" alt="">` : ''}
              <span><b>${escapar(u.patrimonio)}</b> ${u.numero_serie ? `· ${escapar(u.numero_serie)}` : ''}
                <span class="sub-inline">${escapar(u.estado.replace('_', ' '))}</span></span>
              <input type="file" accept="image/*" data-foto-un="${escapar(u.patrimonio)}" hidden>
              <button type="button" class="btn btn-claro pf-foto-btn" data-tirar-foto="${escapar(u.patrimonio)}" hidden>📷 Foto da saída</button>
              <span class="pf-foto-ok" data-foto-ok="${escapar(u.patrimonio)}" hidden>✓ foto ok</span>
            </label>`,
            )
            .join('');

          caixa.addEventListener('change', (e) => {
            const alvo = e.target as HTMLInputElement;
            if (alvo.hasAttribute('data-un')) {
              const pat = alvo.dataset['un']!;
              caixa.querySelector<HTMLElement>(`[data-tirar-foto="${CSS.escape(pat)}"]`)!.hidden =
                !alvo.checked || fotosPorPatrimonio.has(pat);
              return;
            }
            if (alvo.hasAttribute('data-foto-un') && alvo.files?.[0]) {
              const pat = alvo.dataset['fotoUn']!;
              void api
                .enviarFoto('itens', alvo.files[0], pat)
                .then((f) => {
                  fotosPorPatrimonio.set(pat, f.caminho);
                  caixa.querySelector<HTMLElement>(`[data-tirar-foto="${CSS.escape(pat)}"]`)!.hidden = true;
                  caixa.querySelector<HTMLElement>(`[data-foto-ok="${CSS.escape(pat)}"]`)!.hidden = false;
                })
                .catch(() => avisarBloqueante('Não consegui subir a foto. Tente de novo.'));
            }
          });
          caixa.addEventListener('click', (e) => {
            const b = (e.target as HTMLElement).closest<HTMLElement>('[data-tirar-foto]');
            if (!b) return;
            caixa
              .querySelector<HTMLInputElement>(`[data-foto-un="${CSS.escape(b.dataset['tirarFoto']!)}"]`)!
              .click();
          });
        })
        .catch(() => {
          caixa.innerHTML = `<p class="ped-alerta">Não consegui listar os exemplares.</p>`;
        });
    });

    const selMotorista = raiz.querySelector<HTMLSelectElement>('#pf-motorista');
    const avulso = raiz.querySelector<HTMLInputElement>('#pf-motorista-avulso');
    selMotorista?.addEventListener('change', () => {
      if (avulso) {
        avulso.hidden = selMotorista.value !== '__avulso__';
        if (!avulso.hidden) avulso.focus();
      }
    });

    raiz.querySelector('#pf-separar')!.addEventListener('click', () => {
      const itens = [...raiz.querySelectorAll<HTMLElement>('[data-cod]')].map((l) => {
        const escolhidas = [...l.querySelectorAll<HTMLInputElement>('[data-un]:checked')].map(
          (c) => c.dataset['un']!,
        );
        return {
          codigo: l.dataset['cod']!,
          separada: Number(l.querySelector<HTMLInputElement>('[data-sep]')!.value) || 0,
          ...(l.hasAttribute('data-ferramenta') && escolhidas.length
            ? {
                unidades: escolhidas.map((pat) => ({
                  patrimonio: pat,
                  foto: fotosPorPatrimonio.get(pat) ?? '',
                })),
              }
            : {}),
        };
      });
      const semFoto = itens.flatMap((i) => (i.unidades ?? []).filter((u) => !u.foto));
      if (semFoto.length) {
        avisarBloqueante(
          `Falta a foto da saída de: ${semFoto.map((u) => u.patrimonio).join(', ')}.`,
        );
        return;
      }
      const escolhido = selMotorista?.value ?? '';
      const motorista =
        escolhido === '__avulso__' ? (avulso?.value.trim() ?? '') : escolhido.trim();
      // Romaneio sem motorista não existe mais (14/08, Lucas) — cadastrado ou
      // avulso, mas alguém. O servidor também recusa; aqui é o aviso na hora.
      if (!motorista) {
        avisarBloqueante(
          escolhido === '__avulso__'
            ? 'Digite o nome do motorista avulso.'
            : 'Escolha o motorista antes de confirmar — ou use "Outro" para um avulso.',
        );
        return;
      }
      void api
        .post<{ romaneio?: string; parcial?: boolean }>(
          `/api/pedidos/${encodeURIComponent(p.numero)}/separacao`,
          { itens, ...(motorista ? { motorista } : {}) },
        )
        .then((r) => {
          if (r.romaneio) avisar(`Tudo separado — romaneio ${r.romaneio} criado.`);
          else avisar('Separação parcial registrada — o solicitante decide agora.');
          return listar();
        })
        .catch(mostrarErro);
    });

  }

  void listar();
}
